/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useMemo } from 'react';
import { Typography, Box, Grid } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { FlexBox } from 'components/Containers/FlexBox';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  cleanDTCString,
  processAssetChartData,
  processTypeChartData,
  getLimitedTypes,
} from './utils';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface CustomYAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value: string;
  };
}

const CustomYAxisTick = (props: CustomYAxisTickProps) => {
  const maxCharsPerLine = 10;
  const maxTotalChars = 7;
  const { x, y, payload } = props;
  const enableDarkTheme = useAppTheme();

  const originalLabel = cleanDTCString(payload?.value ?? '');
  const isTruncated = originalLabel.length > maxTotalChars;

  const formatLabel = (label: any, forceFull = false) => {
    if (!label) return '';

    let formattedLabel = cleanDTCString(label);

    if (forceFull || formattedLabel.length <= maxTotalChars) {
      return [formattedLabel];
    }
    formattedLabel = formattedLabel.substring(0, maxTotalChars) + '...';

    if (formattedLabel.length > maxCharsPerLine) {
      const lines = [];
      let currentLine = '';
      const words = formattedLabel.split(/(?=[A-Z])|\s+/);

      for (const word of words) {
        if (currentLine.length + word.length > maxCharsPerLine) {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = currentLine ? currentLine + word : word;
        }
      }

      if (currentLine) {
        lines.push(currentLine);
      }

      return lines;
    }

    return [formattedLabel];
  };

  const displayLines = formatLabel(payload?.value, false) || [];

  if (displayLines.length === 0) return null;

  return (
    <g
      transform={`translate(${x},${y})`}
      style={{ cursor: isTruncated ? 'pointer' : 'default' }}
    >
      <title>{isTruncated ? originalLabel : ''}</title>
      {displayLines?.map((line: any, index: number) => (
        <text
          key={index}
          x={0}
          y={index * 12}
          dy={10}
          textAnchor="end"
          fill={(enableDarkTheme ? _palette.dark : _palette.light).textPrimary}
          fontSize={10}
          transform="rotate(-30)"
        >
          {line}
        </text>
      ))}
    </g>
  );
};

export const TypeDTCChart = ({
  enableDarkTheme,
  data,
  selectedAsset,
  selectedType,
  setSelectedType,
  highlightedBar,
  handleBarClick,
  colorMap,
  maxItems = 50,
}: {
  enableDarkTheme: boolean;
  data: any[];
  selectedAsset: string | null;
  setSelectedAsset: any;
  selectedType: string | null;
  setSelectedType: any;
  highlightedBar: string | null;
  handleBarClick: any;
  colorMap: any;
  maxItems?: number;
}) => {
  const { t } = useTranslation();
  const filteredData = useMemo(() => {
    return selectedAsset
      ? data.filter((item) => item.asset === selectedAsset)
      : data;
  }, [data, selectedAsset]);

  const allDtcTypes = useMemo(() => {
    return [...new Set(filteredData.map((item) => item.dtc_type))];
  }, [filteredData]);

  const allDtcCodes = useMemo(() => {
    return [...new Set(filteredData.map((item) => item.dtc_code))];
  }, [filteredData]);

  const dtcTypes = useMemo(() => {
    return getLimitedTypes(allDtcTypes, filteredData, 10);
  }, [allDtcTypes, filteredData]);

  const dtcCodes = useMemo(() => {
    return getLimitedTypes(allDtcCodes, filteredData, 10);
  }, [allDtcCodes, filteredData]);

  const chartData = useMemo(() => {
    if (selectedAsset) {
      return processTypeChartData(filteredData, dtcCodes, maxItems);
    }
    return processAssetChartData(filteredData, dtcTypes, maxItems);
  }, [filteredData, selectedAsset, dtcCodes, dtcTypes, maxItems]);

  const textColor = (enableDarkTheme ? _palette.dark : _palette.light)
    .textPrimary;

  const isDataTruncated = selectedAsset
    ? allDtcTypes.length > chartData.length
    : filteredData.length > chartData.length;

  const showEmptyState = selectedAsset
    ? dtcCodes.length === 0
    : dtcTypes.length === 0;

  return (
    <Grid item lg={5} md={12} sm={12} xs={12}>
      <Box
        sx={{
          marginTop: '20px',
          border: `1px solid ${
            enableDarkTheme
              ? _palette.dark.borderGrey
              : _palette.light.borderGrey
          }`,
          borderRadius: '4px',
          backgroundColor: enableDarkTheme
            ? _palette.dark.boxBackground
            : _palette.light.boxBackground,
          padding: '10px',
          minHeight: '170px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: textColor,
              fontSize: '11px',
            }}
          >
            {selectedAsset ? (
              <>
                {t('sensor_type_distribution_for_asset')}{' '}
                <span
                  style={{
                    color: enableDarkTheme
                      ? _palette.dark.primary
                      : _palette.light.primary,
                    fontSize: '14px',
                  }}
                >
                  {selectedAsset}
                </span>
              </>
            ) : (
              t('asset_wise_sensor_distribution')
            )}
          </Typography>
          {isDataTruncated && (
            <Typography
              sx={{
                color: '#D9A92E',
                fontSize: '10px',
                fontStyle: 'italic',
              }}
            >
              {t('showing_top')} {chartData.length}
            </Typography>
          )}
        </Box>
        {showEmptyState ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <Typography
              sx={{
                color: textColor,
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              {t('no_data_available_for_the_selected_filters')}
            </Typography>
          </Box>
        ) : (
          <FlexBox
            sx={{
              maxWidth: 'inherit',
              paddingTop: '20px',
              marginRight: '10px',
              paddingBottom: '10px',
              height: '400px',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={800}
                height={300}
                data={chartData}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: -45,
                }}
              >
                <CartesianGrid
                  stroke={
                    (enableDarkTheme ? _palette.dark : _palette.light)
                      .cartesianGrid
                  }
                  horizontal={false}
                />
                <XAxis
                  type="category"
                  dataKey="name"
                  tick={<CustomYAxisTick />}
                  height={130}
                  interval="preserveStartEnd"
                  label={{
                    value: selectedAsset
                      ? t('dtc_type_axis')
                      : t('asset_id_axis'),
                    position: 'center',
                    fill: textColor,
                    fontWeight: 'bold',
                    fontFamily: 'Arial',
                    offset: 30,
                    fontSize: '12px',
                    style: {
                      textAnchor: 'middle',
                    },
                  }}
                />
                <YAxis
                  type="number"
                  tick={{
                    fontSize: 11,
                    fill: textColor,
                  }}
                  allowDecimals={false}
                  label={{
                    value: t('count_axis'),
                    angle: -90,
                    offset: 30,
                    position: 'insideLeft',
                    fill: textColor,
                    fontWeight: 'bold',
                    fontFamily: 'Arial',
                    fontSize: '12px',
                    style: {
                      textAnchor: 'middle',
                    },
                  }}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const filteredPayload = payload.filter(
                        (entry) => entry.value !== 0
                      );
                      if (filteredPayload.length === 0) return null;

                      return (
                        <div
                          style={{
                            backgroundColor: '#FFF',
                            opacity: 1,
                            border: `1px solid ${_palette.dark.primary}`,
                            padding: '10px',
                            fontSize: '10px',
                            borderRadius: '5px',
                          }}
                        >
                          {selectedAsset && (
                            <p
                              style={{
                                fontWeight: 'normal',
                              }}
                            >
                              {t(
                                'click_on_this_particular_dtc_type_to_reflect'
                              )}
                              <br />
                              {t(
                                'it_39_s_day_wise_distribution_for_this_asset'
                              )}
                              <br />
                              <br />
                            </p>
                          )}
                          <p>{`${selectedAsset ? 'DTC Type' : 'Asset'}: ${
                            selectedAsset ? cleanDTCString(label) : label
                          }`}</p>
                          {filteredPayload.map((entry: any, index) => (
                            <p
                              key={`item-${index}`}
                              style={{ color: entry.color }}
                            >
                              {cleanDTCString(entry?.name)}: {entry.value}
                            </p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                  cursor={{
                    fill: enableDarkTheme
                      ? _palette.dark.boxBackground
                      : '#CCCCCC',
                  }}
                  offset={30}
                />
                <Legend
                  wrapperStyle={{
                    fontSize: '10px',
                    paddingBottom: '20px',
                  }}
                  formatter={cleanDTCString}
                  verticalAlign="top"
                  iconType="circle"
                  align="left"
                  iconSize={10}
                />
                {(selectedAsset ? dtcCodes : dtcTypes).map((type: string) => (
                  <Bar
                    key={type}
                    dataKey={type}
                    stackId="a"
                    fill={colorMap[type]}
                    name={type}
                    barSize={35}
                    onClick={(data) => {
                      if (selectedAsset) {
                        setSelectedType(data.name);
                        handleBarClick(data);
                      }
                    }}
                    shape={(props: any) => {
                      const { x, y, width, height, payload, fill } = props;
                      const isSelected = payload.name === highlightedBar;

                      return (
                        <rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill={fill}
                          stroke={
                            selectedType && isSelected
                              ? enableDarkTheme
                                ? _palette.dark.primary
                                : _palette.light.primary
                              : 'none'
                          }
                          strokeWidth={isSelected ? 5 : 0}
                          rx={2}
                        />
                      );
                    }}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </FlexBox>
        )}
      </Box>
    </Grid>
  );
};
